import type { Monaco } from './sqlMonacoTypes';
import { plsqlMonarchTokenizer } from './plsqlMonarchTokenizer';

/**
 * PL/SQL keyword completions for the Monaco completion provider.
 * Returns snippet-style items for common PL/SQL constructs.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getPlSqlCompletionItems(monaco: Monaco, range: any) {
  const kind = monaco.languages.CompletionItemKind;
  return [
    {
      label: 'DECLARE...BEGIN...END',
      kind: kind.Snippet,
      insertText: 'DECLARE\n  ${1:v_var} ${2:VARCHAR2(100)};\nBEGIN\n  ${3:NULL;}\nEND;\n/',
      insertTextRules: 4,
      detail: 'PL/SQL anonymous block',
      range,
    },
    {
      label: 'BEGIN...EXCEPTION...END',
      kind: kind.Snippet,
      insertText: 'BEGIN\n  ${1:NULL;}\nEXCEPTION\n  WHEN ${2:OTHERS} THEN\n    ${3:NULL;}\nEND;\n/',
      insertTextRules: 4,
      detail: 'PL/SQL block with exception handler',
      range,
    },
    {
      label: 'IF...THEN...END IF',
      kind: kind.Snippet,
      insertText: 'IF ${1:condition} THEN\n  ${2:NULL;}\nEND IF;',
      insertTextRules: 4,
      detail: 'IF statement',
      range,
    },
    {
      label: 'IF...THEN...ELSE...END IF',
      kind: kind.Snippet,
      insertText: 'IF ${1:condition} THEN\n  ${2:NULL;}\nELSE\n  ${3:NULL;}\nEND IF;',
      insertTextRules: 4,
      detail: 'IF-ELSE statement',
      range,
    },
    {
      label: 'FOR...LOOP...END LOOP',
      kind: kind.Snippet,
      insertText: 'FOR ${1:i} IN ${2:1}..${3:10} LOOP\n  ${4:NULL;}\nEND LOOP;',
      insertTextRules: 4,
      detail: 'FOR loop',
      range,
    },
    {
      label: 'WHILE...LOOP...END LOOP',
      kind: kind.Snippet,
      insertText: 'WHILE ${1:condition} LOOP\n  ${2:NULL;}\nEND LOOP;',
      insertTextRules: 4,
      detail: 'WHILE loop',
      range,
    },
    {
      label: 'CURSOR...FOR LOOP',
      kind: kind.Snippet,
      insertText: 'FOR ${1:rec} IN (${2:SELECT * FROM dual}) LOOP\n  ${3:NULL;}\nEND LOOP;',
      insertTextRules: 4,
      detail: 'Cursor FOR loop',
      range,
    },
    {
      label: 'CREATE OR REPLACE PROCEDURE',
      kind: kind.Snippet,
      insertText: 'CREATE OR REPLACE PROCEDURE ${1:proc_name}(\n  ${2:p_param} IN ${3:VARCHAR2}\n)\nAS\nBEGIN\n  ${4:NULL;}\nEND ${1:proc_name};\n/',
      insertTextRules: 4,
      detail: 'Stored procedure',
      range,
    },
    {
      label: 'CREATE OR REPLACE FUNCTION',
      kind: kind.Snippet,
      insertText: 'CREATE OR REPLACE FUNCTION ${1:func_name}(\n  ${2:p_param} IN ${3:VARCHAR2}\n) RETURN ${4:VARCHAR2}\nAS\nBEGIN\n  RETURN ${5:NULL};\nEND ${1:func_name};\n/',
      insertTextRules: 4,
      detail: 'Stored function',
      range,
    },
    {
      label: 'CREATE OR REPLACE PACKAGE',
      kind: kind.Snippet,
      insertText: 'CREATE OR REPLACE PACKAGE ${1:pkg_name}\nAS\n  ${2:-- declarations}\nEND ${1:pkg_name};\n/',
      insertTextRules: 4,
      detail: 'Package specification',
      range,
    },
    {
      label: 'CREATE OR REPLACE PACKAGE BODY',
      kind: kind.Snippet,
      insertText: 'CREATE OR REPLACE PACKAGE BODY ${1:pkg_name}\nAS\n  ${2:-- implementations}\nEND ${1:pkg_name};\n/',
      insertTextRules: 4,
      detail: 'Package body',
      range,
    },
    {
      label: 'EXCEPTION WHEN...THEN',
      kind: kind.Snippet,
      insertText: 'EXCEPTION\n  WHEN ${1:NO_DATA_FOUND} THEN\n    ${2:NULL;}\n  WHEN OTHERS THEN\n    ${3:RAISE;}\n',
      insertTextRules: 4,
      detail: 'Exception handler block',
      range,
    },
    {
      label: 'BULK COLLECT INTO',
      kind: kind.Snippet,
      insertText: 'BULK COLLECT INTO ${1:v_collection} ${2:LIMIT ${3:100}}',
      insertTextRules: 4,
      detail: 'Bulk collect',
      range,
    },
    {
      label: 'DBMS_OUTPUT.PUT_LINE',
      kind: kind.Function,
      insertText: "DBMS_OUTPUT.PUT_LINE(${1:'message'});",
      insertTextRules: 4,
      detail: 'Print to output buffer',
      range,
    },
    {
      label: 'RAISE_APPLICATION_ERROR',
      kind: kind.Function,
      insertText: "RAISE_APPLICATION_ERROR(${1:-20001}, ${2:'error message'});",
      insertTextRules: 4,
      detail: 'Raise custom error',
      range,
    },
    // PL/SQL keywords and Oracle types as simple completions
    ...[
      'DECLARE', 'BEGIN', 'END', 'EXCEPTION', 'LOOP', 'EXIT WHEN', 'RETURN', 'RAISE',
      'IF', 'ELSIF', 'ELSE', 'THEN', 'CURSOR', 'OPEN', 'FETCH', 'CLOSE', 'FORALL',
      'BULK COLLECT', 'PIPE ROW', 'PRAGMA', 'EXECUTE IMMEDIATE', 'TYPE', 'SUBTYPE',
      'RECORD', 'VARRAY', 'NOCOPY', 'PIPELINED', 'DETERMINISTIC', 'RESULT_CACHE',
    ].map((kw) => ({ label: kw, kind: kind.Keyword, insertText: kw, detail: 'PL/SQL keyword', range })),
    ...[
      'NUMBER', 'VARCHAR2', 'INTEGER', 'PLS_INTEGER', 'BINARY_INTEGER', 'BOOLEAN',
      'DATE', 'TIMESTAMP', 'CLOB', 'BLOB', 'RAW', 'SYS_REFCURSOR', 'XMLTYPE', 'JSON',
    ].map((t) => ({ label: t, kind: kind.TypeParameter, insertText: t, detail: 'Oracle type', range })),
  ];
}

/**
 * Register PL/SQL language with Monaco if not already registered.
 * Sets up Monarch tokenizer, language configuration, and keyword completions.
 */
export function registerPlSqlLanguage(monaco: Monaco): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (monaco.languages.getLanguages().some((lang: any) => lang.id === 'plsql')) return;

  monaco.languages.register({ id: 'plsql', aliases: ['PL/SQL', 'plsql', 'PLSQL'] });
  monaco.languages.setMonarchTokensProvider('plsql', plsqlMonarchTokenizer);

  monaco.languages.setLanguageConfiguration('plsql', {
    comments: { lineComment: '--', blockComment: ['/*', '*/'] },
    brackets: [['(', ')'], ['[', ']'], ['BEGIN', 'END']],
    autoClosingPairs: [
      { open: '(', close: ')' },
      { open: '[', close: ']' },
      { open: "'", close: "'", notIn: ['string'] },
      { open: '"', close: '"', notIn: ['string'] },
      { open: '/*', close: '*/' },
    ],
    surroundingPairs: [
      { open: '(', close: ')' },
      { open: "'", close: "'" },
      { open: '"', close: '"' },
    ],
    folding: {
      markers: {
        start: /^\s*(?:BEGIN|DECLARE|IF|LOOP|CASE|FOR|WHILE)\b/i,
        end: /^\s*END\b/i,
      },
    },
  });

  monaco.languages.registerCompletionItemProvider('plsql', {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    provideCompletionItems: (model: any, position: any, context: any) => {
      const triggerCharacter: string | undefined = context?.triggerCharacter;
      const prevChar =
        position.column > 1
          ? model
              .getLineContent(position.lineNumber)
              .charAt(position.column - 2)
          : '';
      if (!shouldEmitStaticPlsqlCompletions({ prevChar, triggerCharacter })) {
        return { suggestions: [] };
      }
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: position.column,
        endColumn: position.column,
      };
      return { suggestions: getPlSqlCompletionItems(monaco, range) };
    },
  });
}

/**
 * Server-trigger characters whose suggestion lists belong to the SQL resolver
 * pipeline (dotted-access members, bind-variable lookups, bracketed-quoted
 * identifiers). When the cursor sits right after one of these the static
 * PL/SQL keyword / type / snippet vocabulary is wrong noise — Monaco still
 * invokes us on a generic re-trigger, so we self-suppress.
 */
const STATIC_PLSQL_SUPPRESSING_CHARS = new Set(['.', ':', '\\', '[', '"']);

/**
 * Decide whether the static PL/SQL completion list (snippets + keyword +
 * type vocabulary from `getPlSqlCompletionItems`) is appropriate to emit at
 * the current cursor.
 *
 * Pinned by oracle autocomplete-coverage script.33 §4/§5/§8, script.36 §4/§6,
 * script.37 §4 — at `v_emp_row.|salary` the resolver should own the slot and
 * surface the rowtype's column set, but the `plsql` Monaco language's
 * completion provider was unconditionally adding `BEGIN`, `BLOB`, `BOOLEAN`,
 * `BINARY_INTEGER`, `BEGIN...EXCEPTION...END`, … to the suggest widget.
 *
 * The check is intentionally narrow: only the well-known SQL-resolver
 * trigger characters (and the same chars when sitting one position before
 * the cursor, for manual Ctrl+Space invocation) gate emission off. The
 * happy path (bare cursor or mid-identifier letter) keeps the existing
 * statement-start snippet UX intact for PL/SQL Debugger and Query Editor.
 */
export function shouldEmitStaticPlsqlCompletions(args: {
  readonly prevChar: string;
  readonly triggerCharacter: string | undefined;
}): boolean {
  const { prevChar, triggerCharacter } = args;
  if (triggerCharacter && STATIC_PLSQL_SUPPRESSING_CHARS.has(triggerCharacter)) {
    return false;
  }
  if (prevChar && STATIC_PLSQL_SUPPRESSING_CHARS.has(prevChar)) {
    return false;
  }
  return true;
}
